import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Bookmark,
  BookmarkCheck,
  Mail,
  Phone,
  MapPin,
  Loader2,
  FileText,
  Download,
  Lock,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { api, API } from "@/lib/auth-context";
import { useAuth } from "@/lib/auth-context";
import CaseSummary from "@/components/CaseSummary";
import CaseEditDialog from "@/components/CaseEditDialog";

export default function AttorneyCaseDetail() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Edit available to anyone who has standing on this case. Backend re-checks
  // RBAC on every mutation (admin/law_firm anywhere; attorney on claimed;
  // client on own). UI just exposes the button optimistically.
  const canEdit = !!user && ["admin", "law_firm", "attorney"].includes(user.role);
  const [c, setCase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const fetchCase = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/attorney/cases/${caseId}`);
      setCase(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load case");
      navigate("/attorney/dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCase();
    // eslint-disable-next-line
  }, [caseId]);

  const handleToggleSave = async () => {
    try {
      if (c.is_saved) {
        await api.delete(`/attorney/cases/${c.id}/save`);
      } else {
        await api.post(`/attorney/cases/${c.id}/save`);
      }
      setCase({ ...c, is_saved: !c.is_saved });
    } catch (e) {
      toast.error("Action failed");
    }
  };

  const handleClaim = async () => {
    setClaiming(true);
    try {
      await api.post(`/attorney/cases/${c.id}/claim`);
      toast.success("Case claimed — client contact info unlocked");
      await fetchCase();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not claim case");
    } finally {
      setClaiming(false);
    }
  };

  if (loading || !c) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#0A192F]" />
      </div>
    );
  }

  return (
    <div
      data-testid="attorney-case-detail"
      className="max-w-6xl mx-auto px-6 lg:px-8 py-10"
    >
      <div className="flex items-center justify-between mb-6">
        <Link
          to="/attorney/dashboard"
          data-testid="back-to-dashboard"
          className="flex items-center gap-2 text-slate-700 hover:text-[#0A192F]"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-medium">Back to cases</span>
        </Link>
        <div className="flex items-center gap-2">
          {canEdit && (
            <Button
              data-testid="case-edit-open-btn"
              variant="outline"
              onClick={() => setEditOpen(true)}
              className="rounded-lg border-[#C5A059] text-[#0A192F]"
            >
              Edit case
            </Button>
          )}
          {(
            <Button
              variant="outline"
              data-testid="detail-save-btn"
              onClick={handleToggleSave}
              className="rounded-lg border-slate-300 text-[#0A192F]"
            >
              {c.is_saved ? (
                <>
                  <BookmarkCheck className="w-4 h-4 mr-2 text-[#C5A059]" /> Saved
                </>
              ) : (
                <>
                  <Bookmark className="w-4 h-4 mr-2" /> Save case
                </>
              )}
            </Button>
          )}

          {c.is_claimed_by_me ? (
            <span
              data-testid="claimed-badge"
              className="inline-flex items-center gap-1.5 bg-[#C5A059]/15 border border-[#C5A059]/40 text-[#0A192F] px-3 py-2 rounded-lg text-sm font-semibold"
            >
              <CheckCircle2 className="w-4 h-4 text-[#C5A059]" /> Claimed
            </span>
          ) : c.is_claimed ? (
            <span
              data-testid="claimed-other-badge"
              className="inline-flex items-center gap-1.5 bg-slate-100 border border-slate-200 text-slate-600 px-3 py-2 rounded-lg text-sm"
            >
              <Lock className="w-4 h-4" /> Claimed by another attorney
            </span>
          ) : (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  data-testid="claim-btn"
                  disabled={claiming}
                  className="bg-[#0A192F] hover:bg-[#112A46] text-white rounded-lg"
                >
                  {claiming ? <Loader2 className="w-4 h-4 animate-spin" /> : "Claim case"}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-heading text-2xl text-[#0A192F]">
                    Claim this case?
                  </AlertDialogTitle>
                  <AlertDialogDescription className="text-slate-600 leading-relaxed">
                    Claims are <strong>exclusive and first-come-first-served</strong>.
                    Once you claim this case, no other attorney can claim it
                    and the client&apos;s contact information will be unlocked
                    for you. The client will be notified that you are
                    reviewing their matter.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="claim-cancel-btn">Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    data-testid="claim-confirm-btn"
                    onClick={handleClaim}
                    className="bg-[#0A192F] hover:bg-[#112A46]"
                  >
                    Yes, claim it
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      {c.summary && <CaseSummary summary={c.summary} documents={c.documents || []} />}

      {/* Contact */}
      <div className="mt-8 bg-[#0A192F] text-white rounded-2xl p-7">
        <p className="text-xs tracking-[0.22em] uppercase font-semibold text-[#C5A059]">
          Client Contact
        </p>
        {c.contact ? (
          <div className="mt-3 grid md:grid-cols-2 gap-4">
            <div>
              <p className="font-heading text-2xl font-semibold" data-testid="contact-name">{c.contact.full_name}</p>
              <p className="text-sm text-slate-400 mt-1">{[c.contact.city, c.contact.state].filter(Boolean).join(", ")}</p>
            </div>
            <div className="space-y-2 text-sm">
              <p className="flex items-center gap-2"><Mail className="w-4 h-4 text-[#C5A059]" /> <a href={`mailto:${c.contact.email}`} className="underline" data-testid="contact-email">{c.contact.email}</a></p>
              <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-[#C5A059]" /> <a href={`tel:${c.contact.phone}`} className="underline" data-testid="contact-phone">{c.contact.phone}</a></p>
              <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#C5A059]" /> {c.contact.city}, {c.contact.state}</p>
            </div>
          </div>
        ) : c.is_claimed && !c.is_claimed_by_me ? (
          <p data-testid="contact-locked-other" className="mt-3 text-slate-300 text-sm">
            <Lock className="inline w-4 h-4 mr-1.5 text-slate-400" />
            Contact information is locked to the attorney who claimed this case.
          </p>
        ) : (
          <p data-testid="contact-locked" className="mt-3 text-slate-300 text-sm">
            <Lock className="inline w-4 h-4 mr-1.5 text-slate-400" />
            Claim this case to unlock the client&apos;s contact information.
          </p>
        )}
      </div>

      {/* Documents */}
      {c.documents?.length > 0 && (
        <div className="mt-8 bg-white border border-slate-200 rounded-xl p-7">
          <p className="text-xs tracking-[0.22em] uppercase font-semibold text-slate-500 mb-4">
            Uploaded Documents
          </p>
          <ul className="divide-y divide-slate-100">
            {c.documents.map((d) => (
              <li key={d.id} className="py-3 flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-slate-700">
                  <FileText className="w-4 h-4 text-[#C5A059]" />
                  {d.original_filename}
                  <span className="text-xs text-slate-400">
                    {Math.round((d.size || 0) / 1024)} KB
                  </span>
                </div>
                <a
                  href={`${API}/cases/${c.id}/documents/${d.id}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`download-${d.id}`}
                  className="text-sm text-[#0A192F] underline inline-flex items-center gap-1"
                >
                  <Download className="w-3.5 h-3.5" /> Download
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {canEdit && (
        <CaseEditDialog
          caseData={c}
          open={editOpen}
          onOpenChange={setEditOpen}
          onSaved={(updated) => setCase({ ...c, ...updated })}
        />
      )}
    </div>
  );
}
